import { useEffect, useState } from "react";
import { fetchAdminStats, fetchAdminCatalog, toggleExpertPlaceholder } from "@/lib/api";
import { toast } from "sonner";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("oe_admin_token") || "");
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null);

  const load = async (t, d) => {
    setLoading(true);
    setErr(null);
    try {
      const [s, c] = await Promise.all([fetchAdminStats(t, d), fetchAdminCatalog(t)]);
      setStats(s);
      setCatalog(c);
      localStorage.setItem("oe_admin_token", t);
    } catch (e) {
      setErr(e?.response?.data?.detail || "unauthorized");
      setStats(null);
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  };

  const onToggle = async (slug, next) => {
    setToggling(slug);
    try {
      await toggleExpertPlaceholder(token, slug, next);
      setCatalog((c) => ({
        ...c,
        items: c.items.map((i) => (i.slug === slug ? { ...i, placeholder: next } : i)),
      }));
      toast.success(next ? `${slug} → placeholder (hidden from sale)` : `${slug} → LIVE`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "toggle failed");
    } finally {
      setToggling(null);
    }
  };

  useEffect(() => { if (token) load(token, days); }, []); // initial if already saved

  const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <main data-testid="admin-page" className="pt-14 pb-24 md:pb-28">
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-12 font-mono text-xs md:text-sm">
        <div className="text-[10px] uppercase tracking-[0.3em] text-warn mb-2">// admin · aggregate</div>
        <h1 className="font-display font-black text-2xl md:text-3xl tracking-tighter mb-6 leading-tight">
          Sales dashboard
        </h1>

        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">admin token</label>
            <input
              data-testid="admin-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full bg-transparent border border-border focus:border-warn px-3 py-2 focus:outline-none text-sm"
              placeholder="ADMIN_TOKEN env var value"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">days</label>
            <select
              data-testid="admin-days"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-transparent border border-border px-3 py-2 focus:outline-none text-sm"
            >
              {[7, 30, 90, 365].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <button
            data-testid="admin-load-btn"
            onClick={() => load(token, days)}
            disabled={!token || loading}
            className="text-xs font-mono uppercase tracking-[0.2em] bg-warn text-warn-foreground px-4 py-2 disabled:opacity-30 hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            {loading ? "loading…" : "[ load → ]"}
          </button>
        </div>

        {err && <div data-testid="admin-error" className="text-destructive mb-4">[error] {err}</div>}

        {stats && (
          <div className="space-y-6">
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["orders paid",      stats.totals.orders_paid],
                ["revenue",          fmt(stats.totals.revenue_cents), true],
                ["refunds",          stats.totals.orders_refunded],
                ["net",              fmt(stats.totals.net_cents), true],
                ["orders initiated", stats.totals.orders_initiated],
                ["catalog size",     stats.catalog_size],
                ["entitlements",     stats.entitlements_total],
                ["chargebacks",      stats.abuse.chargeback_events],
              ].map(([k, v, accent], i) => (
                <div key={i} data-testid={`admin-stat-${i}`} className="border border-border px-3 py-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
                  <div className={`mt-1 text-lg md:text-xl ${accent ? "text-warn" : "text-foreground"}`}>{v}</div>
                </div>
              ))}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">// top experts</div>
              {stats.top_experts.length === 0 ? (
                <div className="text-muted-foreground">[no sales yet]</div>
              ) : (
                <ul className="divide-y divide-border">
                  {stats.top_experts.map((r) => (
                    <li key={r.slug} className="grid grid-cols-[1fr_auto] py-2">
                      <span>{r.slug}</span>
                      <span className="text-warn">×{r.sold}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">// daily</div>
              {stats.daily.length === 0 ? (
                <div className="text-muted-foreground">[no paid orders in window]</div>
              ) : (
                <ul className="divide-y divide-border">
                  <li className="grid grid-cols-[1fr_auto_auto] gap-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span>date</span><span>orders</span><span>revenue</span>
                  </li>
                  {stats.daily.map((d) => (
                    <li key={d.date} className="grid grid-cols-[1fr_auto_auto] gap-4 py-2">
                      <span>{d.date}</span>
                      <span>{d.count}</span>
                      <span className="text-warn">{fmt(d.revenue_cents)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">// installs · reach</div>
              {stats.installs ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[["apk", "app"], ["engine", "engine"]].map(([k, label]) => {
                    const b = stats.installs[k] || {};
                    return (
                      <div key={k} className="border border-border p-3" data-testid={`install-${k}`}>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{label} downloads</div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <div className="text-[10px] text-muted-foreground">clicks · all time</div>
                            <div className="text-lg text-warn">{b.clicks_all ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">clicks · 7d</div>
                            <div className="text-lg">{b.clicks_7d ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">distinct devices · 7d</div>
                            <div className="text-lg">{b.distinct_fingerprints_7d ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-muted-foreground">distinct origins · 7d</div>
                            <div className="text-lg">{b.distinct_ips_7d ?? 0}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-muted-foreground">[no download telemetry yet]</div>
              )}
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">// safety gate · last 7 days</div>
              {stats.safety_gate ? (
                <div className="border border-border">
                  <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="px-3 py-3" data-testid="gate-prompts">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">prompts</div>
                      <div className="mt-1 text-lg md:text-xl">{stats.safety_gate.prompts_7d}</div>
                    </div>
                    <div className="px-3 py-3" data-testid="gate-refused">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">refused</div>
                      <div className="mt-1 text-lg md:text-xl text-warn">{stats.safety_gate.refusals_7d}</div>
                    </div>
                    <div className="px-3 py-3" data-testid="gate-rate">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">refuse rate</div>
                      <div className="mt-1 text-lg md:text-xl">{stats.safety_gate.refusal_rate_pct}%</div>
                    </div>
                  </div>
                  {stats.safety_gate.categories.length > 0 && (
                    <ul className="border-t border-border divide-y divide-border">
                      {stats.safety_gate.categories.map((c) => (
                        <li key={c.name} className="grid grid-cols-[1fr_auto] px-3 py-2 text-xs">
                          <span className="text-muted-foreground">{c.name}</span>
                          <span className="text-warn">×{c.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {stats.safety_gate.refusals_7d === 0 && (
                    <div className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
                      [no refusals in the last week]
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-muted-foreground">[no data]</div>
              )}
            </section>

            <div className="text-[10px] text-muted-foreground/70">
              refusal ledger events (all time): {stats.abuse.refusal_events}
            </div>
          </div>
        )}

        {catalog && (
          <section data-testid="admin-catalog" className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
              // catalog · flip to sell
              {!catalog.cdn_base && (
                <span className="ml-2 text-warn/80">[EXPERT_CDN_BASE not set — live rows will still show &quot;coming soon&quot; until the CDN is configured]</span>
              )}
            </div>
            <ul className="divide-y divide-border border-y border-border">
              <li className="grid grid-cols-[1fr_60px_60px_100px_100px] gap-3 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span>expert</span><span className="text-right">GB</span><span className="text-center">free</span><span className="text-center">state</span><span className="text-right">action</span>
              </li>
              {catalog.items.map((i) => (
                <li key={i.slug} className="grid grid-cols-[1fr_60px_60px_100px_100px] gap-3 px-3 py-2 items-center">
                  <div className="min-w-0">
                    <div className="truncate">{i.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{i.slug} · {i.domain}</div>
                  </div>
                  <span className="text-right text-muted-foreground">{i.size_gb}</span>
                  <span className="text-center text-muted-foreground">{i.is_free ? "yes" : "—"}</span>
                  <span className={`text-center text-[10px] uppercase tracking-[0.15em] ${i.placeholder ? "text-muted-foreground" : "text-warn"}`}>
                    {i.placeholder ? "placeholder" : "live"}
                  </span>
                  <button
                    data-testid={`toggle-${i.slug}`}
                    disabled={toggling === i.slug}
                    onClick={() => onToggle(i.slug, !i.placeholder)}
                    className="justify-self-end text-[10px] uppercase tracking-[0.2em] border border-border hover:border-warn hover:text-warn px-2 py-1 transition-colors disabled:opacity-30"
                  >
                    {toggling === i.slug ? "…" : (i.placeholder ? "[ go live → ]" : "[ hide ]")}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[10px] text-muted-foreground/70 leading-relaxed">
              Placeholder = checking-only (won&apos;t appear in <code>/library</code> as available, checkout returns 409).
              Live = downloadable if a <code>download_url</code> or <code>EXPERT_CDN_BASE</code> resolves to a real <code>.expert</code> file for that slug.
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
